import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface SendConsignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consignmentId: string;
  onSuccess: () => void;
}

export function SendConsignmentModal({
  open,
  onOpenChange,
  consignmentId,
  onSuccess,
}: SendConsignmentModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [sourceWarehouseId, setSourceWarehouseId] = useState('');

  // Get warehouses (excluding consignment type)
  const { data: warehousesData } = trpc.warehouse.list.useQuery();
  const sourceWarehouses = (warehousesData as { id: string; code: string; name: string; type: string; isActive: boolean }[] | undefined)?.filter(
    (w: { type: string; isActive: boolean }) => w.type !== 'CONSIGNMENT' && w.isActive
  ) || [];

  const warehouseOptions = sourceWarehouses.map((w: { id: string; code: string; name: string }) => ({
    value: w.id,
    label: `${w.code} - ${w.name}`,
  }));

  const sendMutation = trpc.tradingConsignment.send.useMutation({
    onSuccess: () => {
      showSuccessToast(t('consignment.status.sent'));
      onSuccess();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleSubmit = () => {
    if (!sourceWarehouseId) return;
    sendMutation.mutate({
      id: consignmentId,
      sourceWarehouseId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('consignment.send.title')}</DialogTitle>
          <DialogDescription>{t('consignment.send.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t('consignment.send.warning')}</AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>{t('consignment.send.sourceWarehouse')}</Label>
            <Combobox
              options={warehouseOptions}
              value={sourceWarehouseId}
              onValueChange={setSourceWarehouseId}
              placeholder={t('consignment.send.selectSource')}
              searchPlaceholder={tCommon('search')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sendMutation.isPending}>
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!sourceWarehouseId || sendMutation.isPending}>
            {sendMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('consignment.send.confirmSend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
